"use client";
import { Slot } from "@gigpro/ui/Slot";
import { cn } from "@gigpro/ui/tailwind/cn";
import type * as React from "react";
import { useIsFocused } from "../../utils/useIsFocused";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  ref?: React.Ref<HTMLInputElement>;
  /**
   * Apply error styling to the input.
   */
  invalid?: boolean;

  /**
   * The className value to pass to the div that wraps the input.
   */
  className?: string;

  /**
   * The className value to pass to the `input`.
   */
  inputClassName?: string;

  /**
   * A react node that renders at the start of the input inside of the wrapper.
   * Note: Use `InputAdornment` for the best experience. It will provide some sensible defaults for
   * styling around whatever you render in the input.
   */
  startAdornment?: React.ReactNode;

  /**
   * A react node that renders at the end of the input inside of the wrapper.
   * Note: Use `InputAdornment` for the best experience. It will provide some sensible defaults for
   * styling around whatever you render in the input.
   */
  endAdornment?: React.ReactNode;

  asChild?: boolean;
}

const Input = ({
  ref,
  type,
  disabled,
  readOnly,
  invalid,
  startAdornment,
  endAdornment,
  inputClassName,
  className,
  onFocus,
  onBlur,
  asChild = false,
  ...props
}: InputProps) => {
  const { isFocused, ...focusHandlers } = useIsFocused({ onFocus, onBlur });
  const Comp = asChild ? Slot : "input";
  return (
    <div
      className={cn(
        "inline-flex w-full items-center rounded-sm border-2 bg-white text-black",
        (disabled || readOnly) && "bg-background [&>input]:bg-background",
        /**
         * Order of the border color definitions below matters.
         * Focused should take priority over default, and error should take priority over both.
         */
        (!invalid || disabled || readOnly) && "border-medium-gray",
        isFocused && "border-brand",
        invalid && !disabled && "border-error",
        !!startAdornment && "pl-2",
        !!endAdornment && "pr-2",
        className,
      )}
    >
      {startAdornment}
      <Comp
        type={type}
        className={cn(
          "w-full rounded-sm px-3 py-2 text-large/5 font-regular outline-none placeholder:text-medium-gray",
          inputClassName,
        )}
        ref={ref}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={invalid}
        {...props}
        {...focusHandlers}
      />
      {endAdornment}
    </div>
  );
};
Input.displayName = "Input";

export { Input };
